/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material'
import React, { useState } from 'react'

import {
  CREATE_DISPLAY,
  GET_MASTER_CATEGORIES,
  GET_TENANT_DISPLAYS,
  GetMasterCategoriesQuery,
  GetTenantDisplaysQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

interface Props {
  open: boolean
  onClose: () => void
  storeId: string
  onCreated?: (displayId: string) => void
}

export const CreateDisplayDialog: React.FC<Props> = ({
  open,
  onClose,
  storeId,
  onCreated,
}) => {
  const { t } = useT()
  const [name, setName] = useState('')
  const [masterCategoryId, setMasterCategoryId] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: masterCategoriesData, loading: masterCategoriesLoading } =
    useQuery<GetMasterCategoriesQuery>(GET_MASTER_CATEGORIES, {
      skip: !open,
    })

  const [createDisplay, { loading: creating }] = useMutation(CREATE_DISPLAY, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_TENANT_DISPLAYS,
        variables: {
          filters: { store: { id: storeId } },
          pagination: { limit: 100, offset: 0 },
        } as GetTenantDisplaysQueryVariables,
      },
    ],
  })

  const masterCategories = masterCategoriesData?.masterCategories.results || []

  const handleSave = async () => {
    setError(null)
    try {
      const result = await createDisplay({
        variables: {
          data: {
            name,
            masterCategoryId: masterCategoryId || undefined,
            store: { set: storeId },
          },
        },
      })
      if (result.data.createDisplay.errors?.length) {
        setError(result.data.createDisplay.errors[0].message)
        return
      }
      const newDisplayId = result.data?.createDisplay?.id
      if (newDisplayId) {
        onCreated?.(newDisplayId)
      }
      handleClose()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('display.createError', 'Failed to create display'),
      )
    }
  }

  const handleClose = () => {
    setName('')
    setMasterCategoryId('')
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('display.createNew', 'Create New Display')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('display.name', 'Display Name')}
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t('display.masterCategory', 'Category')}</InputLabel>
            <Select
              value={masterCategoryId}
              label={t('display.masterCategory', 'Category')}
              onChange={e => setMasterCategoryId(e.target.value)}
              disabled={masterCategoriesLoading}
            >
              <MenuItem value="">
                <em>{t('display.noCategory', 'None')}</em>
              </MenuItem>
              {masterCategories.map(category => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={creating}>
          {t('importPlanogram.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || creating}
        >
          {creating
            ? t('display.creating', 'Creating...')
            : t('display.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateDisplayDialog
